/* Shared locale-aware cart module. Mutations are locked and uncertain bundle results are reconciled before retry. */
(() => {
  if (window.LilPilloCart) return;
  let mutationPending = false;
  const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;

  async function request(path, options = {}, timeout = 15000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(route(path), {
        ...options,
        signal: controller.signal,
        headers: { Accept: 'application/json', ...options.headers }
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.description || data.message || 'The basket could not be updated.');
        error.rejected = true;
        error.status = response.status;
        throw error;
      }
      return data;
    } finally {
      clearTimeout(timer);
    }
  }

  async function locked(operation) {
    if (mutationPending) {
      const error = new Error('Please wait for the basket to finish updating.');
      error.rejected = true;
      throw error;
    }
    mutationPending = true;
    try {
      return await operation();
    } finally {
      mutationPending = false;
    }
  }

  const mutate = (path, options) => locked(() => request(path, { method: 'POST', ...options }));

  function quantities(lines = []) {
    return lines.reduce((totals, line) => {
      const id = String(line.variant_id || line.id);
      totals[id] = (totals[id] || 0) + Number(line.quantity || 0);
      return totals;
    }, {});
  }

  function requestedQuantities(items) {
    return items.reduce((totals, item) => {
      const id = String(item.id);
      totals[id] = (totals[id] || 0) + Number(item.quantity || 1);
      return totals;
    }, {});
  }

  function compareAddition(before, after, requested) {
    const beforeTotals = quantities(before?.items);
    const afterTotals = quantities(after?.items);
    let exact = true;
    let changed = false;
    Object.entries(requested).forEach(([id, quantity]) => {
      const delta = (afterTotals[id] || 0) - (beforeTotals[id] || 0);
      if (delta !== quantity) exact = false;
      if (delta > 0) changed = true;
    });
    return { exact, changed };
  }

  async function addBundle(form, extras = []) {
    return locked(async () => {
      const fields = new FormData(form);
      const mainId = String(fields.get('id') || '');
      const mainQuantity = Math.max(1, Number.parseInt(fields.get('quantity'), 10) || 1);
      const items = [{ id: mainId, quantity: mainQuantity }, ...extras.map((item) => ({ id: String(item.id), quantity: Number(item.quantity) || 1 }))];
      const requested = requestedQuantities(items);
      const before = await request('cart.js');
      let response;
      let originalError;

      try {
        response = await request('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        const returned = requestedQuantities(response.items || []);
        if (Object.entries(requested).every(([id, quantity]) => returned[id] === quantity)) return response;
      } catch (error) {
        originalError = error;
      }

      try {
        const after = await request('cart.js');
        const result = compareAddition(before, after, requested);
        if (result.exact) return { items: after.items, reconciled: true };
        if (result.changed) {
          const partial = new Error('Only part of this selection reached your bag. Check the bag before trying again.');
          partial.partial = true;
          partial.uncertain = true;
          partial.rejected = Boolean(originalError?.rejected);
          throw partial;
        }
        if (originalError?.rejected) throw originalError;
      } catch (reconcileError) {
        if (reconcileError.partial || reconcileError === originalError) throw reconcileError;
      }

      const uncertain = originalError || new Error('We could not confirm whether the selection was added.');
      uncertain.uncertain = true;
      throw uncertain;
    });
  }

  async function refresh() {
    const cart = await request('cart.js');
    document.querySelectorAll('[data-lp-bag-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.setAttribute('aria-label', `Bag, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
    });
    return cart;
  }

  function init(root, { onRefresh = () => window.location.reload() } = {}) {
    if (!root || root.dataset.cartInitialized) return;
    root.dataset.cartInitialized = 'true';
    let pending = false;
    const status = root.querySelector('[data-lp-cart-status]');
    const announce = (value) => { if (status) status.textContent = value; };
    async function run(path, options) {
      if (pending || mutationPending) return;
      pending = true;
      root.setAttribute('aria-busy', 'true');
      announce('Updating your basket…');
      let saved = false;
      try {
        await mutate(path, options);
        saved = true;
        await refresh();
        await onRefresh();
      } catch (error) {
        announce(saved ? 'Your change was saved. The basket display could not refresh. Open the full basket to see it; do not repeat the change.' : error.rejected ? error.message : 'We could not confirm the update. Check the full basket before trying again.');
      } finally {
        pending = false;
        root.removeAttribute('aria-busy');
      }
    }
    root.addEventListener('submit', (event) => {
      if (event.target.matches('.cart-form') && event.submitter?.name === 'update') {
        event.preventDefault();
        run('cart/update.js', { body: new FormData(event.target) });
      }
    });
    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-lp-cart-remove]');
      if (!link) return;
      event.preventDefault();
      run('cart/change.js', { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line: Number(link.dataset.line), quantity: 0 }) });
    });
  }

  window.LilPilloCart = {
    init,
    refresh,
    request,
    add: (form) => mutate('cart/add.js', { body: new FormData(form) }),
    addBundle,
    get pending() { return mutationPending; }
  };
  document.querySelectorAll('[data-lp-cart]').forEach((root) => init(root));
})();
